/**
 * Pacific Timezone Helper
 * 
 * Provides utilities to handle Pacific Time (PST/PDT) conversions
 * ensuring consistency between countdown timers and phase detection.
 */

class PacificTimezoneHelper {
  /**
   * Check if a date falls during Daylight Saving Time in US Pacific timezone
   * DST runs from second Sunday in March to first Sunday in November
   */
  static isDaylightSavingTime(date) {
    const year = date.getFullYear();
    
    // Find second Sunday in March (2 AM)
    const marchStart = new Date(year, 2, 1, 2, 0, 0); // March 1, 2 AM
    const marchFirstSunday = new Date(year, 2, (7 - marchStart.getDay() + 1), 2, 0, 0);
    const marchSecondSunday = new Date(marchFirstSunday.getTime() + (7 * 24 * 60 * 60 * 1000));
    
    // Find first Sunday in November (2 AM)
    const novStart = new Date(year, 10, 1, 2, 0, 0); // November 1, 2 AM
    const novFirstSunday = new Date(year, 10, (7 - novStart.getDay() + 1), 2, 0, 0);
    
    return date >= marchSecondSunday && date < novFirstSunday;
  }

  /**
   * Parse a datetime string as Pacific Time and return Date object
   * @param {string} dateStr - Format: "YYYY-MM-DD"
   * @param {string} timeStr - Format: "HH:MM"
   * @returns {Date} - Date object in Pacific timezone
   */
  static parsePacificTime(dateStr, timeStr) {
    if (!dateStr || !timeStr) return null;

    // Create a temporary date to check DST status
    const tempDate = new Date(`${dateStr}T${timeStr}:00`);
    const isPDT = this.isDaylightSavingTime(tempDate);
    const offset = isPDT ? '-07:00' : '-08:00';
    
    // Create the proper Pacific timezone date
    return new Date(`${dateStr}T${timeStr}:00${offset}`);
  }

  /**
   * Get Pacific timezone offset in hours for a given date
   * @param {Date} date - The date to check
   * @returns {number} - Offset in hours (-7 for PDT, -8 for PST)
   */
  static getPacificOffset(date) {
    return this.isDaylightSavingTime(date) ? -7 : -8;
  }

  /**
   * Convert a Date object to Pacific timezone timestamp
   * @param {Date} date - The date to convert
   * @returns {number} - Unix timestamp in milliseconds
   */
  static toPacificTimestamp(date) {
    if (!date) return 0;
    return date.getTime();
  }

  /**
   * Check if we're currently in a time countdown period
   * @param {string} endDateStr - Format: "YYYY-MM-DD"
   * @param {string} endTimeStr - Format: "HH:MM"
   * @returns {boolean} - True if countdown is still active
   */
  static isCountdownActive(endDateStr, endTimeStr) {
    const endDate = this.parsePacificTime(endDateStr, endTimeStr);
    if (!endDate) return false;
    
    const now = new Date();
    return now < endDate;
  }

  /**
   * Get the current campaign phase
   * @param {boolean} hasInventory - Whether product has inventory
   * @param {string} startDateStr - Campaign start date "YYYY-MM-DD"
   * @param {string} startTimeStr - Campaign start time "HH:MM"
   * @param {string} endDateStr - Campaign end date "YYYY-MM-DD"
   * @param {string} endTimeStr - Campaign end time "HH:MM"
   * @returns {string} - Phase: 'during_product_countdown', 'during_time_countdown', or 'after_all'
   */
  static getCampaignPhase(hasInventory, startDateStr, startTimeStr, endDateStr, endTimeStr) {
    // If product has inventory, always show product countdown phase
    if (hasInventory) {
      return 'during_product_countdown';
    }

    // Product sold out - check time countdown
    const now = new Date();
    const startDate = startDateStr && startTimeStr ? this.parsePacificTime(startDateStr, startTimeStr) : null;
    const endDate = endDateStr && endTimeStr ? this.parsePacificTime(endDateStr, endTimeStr) : null;

    // If we have a start date and we're before it
    if (startDate && now < startDate) {
      return 'sold_out_before_start';
    }

    // If we have an end date and we're before it (countdown active)
    if (endDate && now < endDate) {
      return 'during_time_countdown';
    }

    // Otherwise, campaign is over
    return 'after_all';
  }
}

// Export for use in other scripts
if (typeof module !== 'undefined' && module.exports) {
  module.exports = PacificTimezoneHelper;
}

// Make available globally
window.PacificTimezoneHelper = PacificTimezoneHelper;

